import Box from '@/common/components/Box'
import ButtonLink from '@/common/components/ButtonLink'
import InfoField from '@/common/components/InfoField'
import { RewardTicketData } from '@/common/types/rewardTickets.type'
import { MarkAsWinnerRaffleModal } from './MarkAsWinnerRaffleModal'
import { RejectRewardTicketModal } from './RejectRewardTicketModal'
import { twMerge } from 'tailwind-merge'
import { Dispatch, SetStateAction } from 'react'
import { Tooltip } from 'react-tooltip'

interface RewardTicketProps {
  rewardTicket: RewardTicketData
  raffleId?: string
  isNextToAccept?: boolean
  setIsError: Dispatch<SetStateAction<boolean>>
  setMessages: Dispatch<SetStateAction<boolean | string[]>>
}

export default function RewardTicket({
  rewardTicket,
  raffleId,
  isNextToAccept,
  setIsError,
  setMessages,
}: RewardTicketProps) {
  const winPosition = rewardTicket?.win_position 
  const isRewardTicketWinner = winPosition === 1
  const ticketIsRejected = rewardTicket.status === 'rejected'
  const ticketIsAccepted = rewardTicket.status === 'accepted'

  const showRejectTicketButton = ticketIsAccepted || !ticketIsRejected

  const maxPreviewRejectReasonLength = 220

  const fullRejectReason = rewardTicket.reject_reason || '';
  const lastWordSpaceIndex = fullRejectReason.slice(0, maxPreviewRejectReasonLength)?.lastIndexOf(' ')
  const shortRejectReason = fullRejectReason.length > maxPreviewRejectReasonLength ? `${fullRejectReason.slice(0, lastWordSpaceIndex)}...` : fullRejectReason;

  return (
    <Box 
      className={twMerge(
        "flex flex-col flex-1 bg-white-100 gap-4",
        ticketIsAccepted && "bg-success-op",
        ticketIsRejected && "bg-danger-op"
      )}
    >
      <div className='flex items-center justify-between gap-4'>
        <span className='text-base font-bold flex-1'>
          {isRewardTicketWinner ? 'Ganhador' : `${winPosition}º colocado`}
        </span>

        <div className='flex items-center gap-2 flex-1 justify-end'>
          {rewardTicket.reject_reason && (
            <>
              <p
                data-tooltip-id={rewardTicket.id}
                data-tooltip-place="top"
                data-tooltip-html={fullRejectReason}
                className="line-clamp-3 italic text-sm "
              >
                {shortRejectReason || '--'}
              </p>
              {fullRejectReason.length > maxPreviewRejectReasonLength && (
                <Tooltip
                  id={rewardTicket.id}
                  className="z-50 max-w-[500px] whitespace-pre-wrap break-words bg-[#1f1f1f] p-4 border border-gray-700 text-white-900 text-sm"
                />
              )}
            </>
          )}
          {showRejectTicketButton && (
            <RejectRewardTicketModal 
              rewardTicketId={rewardTicket.id} 
              raffleId={raffleId} 
              setIsError={setIsError}
              setMessages={setMessages}
            />
          )}
          {isNextToAccept && (
            <MarkAsWinnerRaffleModal 
              rewardTicketId={rewardTicket.id} 
              raffleId={raffleId} 
              setIsError={setIsError}
              setMessages={setMessages}
            />
          )}
        </div>
      </div>

      <div className='flex items-center justify-between'>
        <InfoField 
          className='flex-initial bg-transparent p-0'
          label='Nome' 
          labelClassName='italic'
          customInfo={
            <ButtonLink
              linkTo={`/tmais-users/${rewardTicket.user.id}`}
              operationKlassToCheckPermission='Admin::Tmais::Users::Show'
            >
              {rewardTicket.user?.full_name}
            </ButtonLink>
          } 
        />
        <InfoField 
          className='flex-initial bg-transparent p-0 gap-0'
          label='Número sorteado' 
          labelClassName='italic'
          value={rewardTicket.number}
        />
      </div>

      <InfoField
        label='Email'
        value={rewardTicket.user?.email}
        className='flex-initial bg-transparent p-0 gap-0'
        labelClassName='italic'
        valueClassName='text-sm'
      />
    </Box>
  )
}
